import {
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { adminBaseApi, baseApi, salesBaseApi } from "./api";
import {
  authSliceActions,
  authSliceReducer,
  authSliceReducerpath,
} from "./features/auth/auth.slice";

interface ErrorPayload {
  status: number;
  data?: {
    message: string;
    code: number;
    data: object;
  };
}

/**
 * @description Middleware for logging or toast notifications
 * @returns next func
 */
export const rtkQueryResponseFeedbackMiddleware: Middleware =
  () => (next) => (action) => {
    const dispatch = store.dispatch;

    if (isRejectedWithValue(action)) {
      const errorPayload = action.payload as ErrorPayload;

      if (
        errorPayload?.status === 401 &&
        errorPayload?.data?.message?.toLowerCase() === "session expired"
      ) {
        dispatch(authSliceActions.signUserOut());
        dispatch(authSliceActions.signAdminOut());
        dispatch(authSliceActions.signSalesOut());

        toast.error("Session expired, please login");
      }
    }

    return next(action);
  };

const rootReducer = combineReducers({
  [authSliceReducerpath]: authSliceReducer,
  [baseApi.reducerPath]: baseApi.reducer,
  [adminBaseApi.reducerPath]: adminBaseApi.reducer,
  [salesBaseApi.reducerPath]: salesBaseApi.reducer,
});

const persistConfig = {
  key: "root",
  storage,
  blacklist: [
    baseApi.reducerPath,
    adminBaseApi.reducerPath,
    salesBaseApi.reducerPath,
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      baseApi.middleware,
      adminBaseApi.middleware,
      salesBaseApi.middleware,
      rtkQueryResponseFeedbackMiddleware,
    ]);
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
